import React, { useState, useEffect } from 'react';
import { Modal, Form, Select, Button, message, Divider, Typography, Checkbox } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import axios from 'axios';
import { API_URL } from '../config';

const { Text, Title } = Typography;

interface PushToEmModalProps {
  visible: boolean;
  onClose: () => void;
  entityType: 'room-type' | 'room';
  entityId: number;
  entityData: any;
  emHotelId?: number;
  emCategories?: any[]; // For rooms: list of EM categories to select from
  roomFeatures?: any[]; // For rooms: features to optionally push as tags
  onSuccess?: () => void;
}

interface EmLanguage {
  id: number;
  code: string;
}

const PushToEmModal: React.FC<PushToEmModalProps> = ({
  visible,
  onClose,
  entityType,
  entityId,
  entityData,
  emHotelId: initialEmHotelId,
  emCategories = [],
  roomFeatures = [],
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [emLanguages, setEmLanguages] = useState<EmLanguage[]>([]);
  const [emHotels, setEmHotels] = useState<{ id: number; name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [languagesLoaded, setLanguagesLoaded] = useState(false);

  // Determine which languages are available based on entity type
  const sqliteLanguages = entityData?.emNames
    ? Object.keys(entityData.emNames)
    : entityData?.names
    ? Object.keys(entityData.names)
    : [];

  useEffect(() => {
    if (visible) {
      setLanguagesLoaded(false);
      if (initialEmHotelId) {
        // If emHotelId is provided, fetch selected languages for that hotel
        loadSelectedEmLanguages(initialEmHotelId);
      } else {
        // Otherwise load all languages
        loadEmLanguages();
      }
      loadEmHotels();
      // For rooms, select all features by default
      if (entityType === 'room' && roomFeatures.length > 0) {
        setSelectedFeatures(roomFeatures.map(f => f.mewsId));
      }
    } else {
      // Reset when modal closes
      setLanguagesLoaded(false);
    }
  }, [visible]);

  // Auto-match language mappings when EM languages are loaded
  useEffect(() => {
    if (emLanguages.length > 0 && sqliteLanguages.length > 0) {
      const autoMappings: Record<string, number> = {};
      // Map sqlite language codes to possible EM language names/codes
      const LANGUAGE_MATCH_MAP: Record<string, string[]> = {
        'en-US': ['english', 'en', 'en-us', 'en-gb'],
        'en-GB': ['english', 'en', 'en-us', 'en-gb'],
        'nl-NL': ['dutch', 'nl', 'nl-nl', 'nederlands'],
        'de-DE': ['german', 'de', 'de-de', 'deutsch'],
        'fr-FR': ['french', 'fr', 'fr-fr', 'français'],
        'es-ES': ['spanish', 'es', 'es-es', 'español'],
        'it-IT': ['italian', 'it', 'it-it', 'italiano'],
      };

      for (const sqliteLang of sqliteLanguages) {
        const possibleMatches = LANGUAGE_MATCH_MAP[sqliteLang] || [sqliteLang.split('-')[0], sqliteLang];
        const match = emLanguages.find(emLang =>
          possibleMatches.some(m => emLang.code.toLowerCase() === m)
        );
        if (match) {
          autoMappings[`mapping_${sqliteLang}`] = match.id;
        }
      }

      if (Object.keys(autoMappings).length > 0) {
        form.setFieldsValue(autoMappings);
      }
    }
  }, [emLanguages, sqliteLanguages.length]);

  const loadEmLanguages = async () => {
    if (languagesLoaded) return;
    try {
      const response = await axios.get(`${API_URL}/em-push/languages`);
      setEmLanguages(response.data);
      setLanguagesLoaded(true);
    } catch (error) {
      console.error('Failed to load EM languages');
    }
  };

  const loadSelectedEmLanguages = async (emHotelId: number) => {
    if (languagesLoaded) return;
    try {
      const response = await axios.get(`${API_URL}/em-hotels/${emHotelId}/languages`);
      // Map the language codes to the format we need (with IDs from all languages)
      const allLanguagesResponse = await axios.get(`${API_URL}/em-push/languages`);
      const allLanguages = allLanguagesResponse.data;

      // Filter to only include languages selected for this hotel group
      const selectedCodes = response.data.map((lang: any) => lang.code);
      const filtered = allLanguages.filter((lang: EmLanguage) =>
        selectedCodes.includes(lang.code)
      );

      setEmLanguages(filtered);
      setLanguagesLoaded(true);
    } catch (error) {
      console.error('Failed to load selected EM languages');
      // Fallback to all languages
      loadEmLanguages();
    }
  };

  const loadEmHotels = async () => {
    try {
      const response = await axios.get(`${API_URL}/em-hotels`);
      setEmHotels(response.data);
    } catch (error) {
      console.error('Failed to load EM hotels');
    }
  };

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      const languageMappings = sqliteLanguages
        .filter(lang => values[`mapping_${lang}`])
        .map(lang => ({
          sqliteLanguage: lang,
          emLanguageId: values[`mapping_${lang}`],
        }));

      if (languageMappings.length === 0) {
        message.warning('Please map at least one language');
        setLoading(false);
        return;
      }

      const emHotelId = values.emHotelId || initialEmHotelId;
      if (!emHotelId) {
        message.error('Please select an EM hotel');
        setLoading(false);
        return;
      }

      if (entityType === 'room-type') {
        // Push room type
        await axios.post(`${API_URL}/em-push/room-type`, {
          roomTypeId: entityId,
          emHotelId,
          languageMappings,
          emNames: entityData?.emNames,
          emDescriptions: entityData?.emDescriptions,
        });
        message.success('Room type pushed to EM successfully');
      } else if (entityType === 'room') {
        // For rooms, category is required
        if (!values.emCategoryId) {
          message.error('Please select an EM category (room type)');
          setLoading(false);
          return;
        }

        // Push room with optional features
        await axios.post(`${API_URL}/em-push/room`, {
          roomId: entityId,
          emHotelId,
          emCategoryId: values.emCategoryId,
          languageMappings,
          featureIds: selectedFeatures.length > 0 ? selectedFeatures : undefined,
          emNames: entityData?.emNames,
          emDescriptions: entityData?.emDescriptions,
        });
        message.success('Room pushed to EM successfully' +
          (selectedFeatures.length > 0 ? ` with ${selectedFeatures.length} features` : ''));
      }

      form.resetFields();
      onSuccess?.();
      onClose();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to push to EM');
    } finally {
      setLoading(false);
    }
  };

  const stripHtml = (html: string) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const handleFeatureToggle = (mewsId: string) => {
    setSelectedFeatures(prev =>
      prev.includes(mewsId)
        ? prev.filter(id => id !== mewsId)
        : [...prev, mewsId]
    );
  };

  const handleSelectAllFeatures = () => {
    setSelectedFeatures(roomFeatures.map(f => f.mewsId));
  };

  const handleDeselectAllFeatures = () => {
    setSelectedFeatures([]);
  };

  return (
    <Modal
      title={`Push ${entityType === 'room-type' ? 'Room Type' : 'Room'} to EM`}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1200}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={initialEmHotelId ? { emHotelId: initialEmHotelId } : {}}
      >
        {!initialEmHotelId && (
          <Form.Item
            label="EM Hotel"
            name="emHotelId"
            rules={[{ required: true, message: 'Please select an EM hotel' }]}
          >
            <Select
              placeholder="Select EM Hotel"
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={emHotels.map(hotel => ({
                value: hotel.id,
                label: hotel.name,
              }))}
            />
          </Form.Item>
        )}

        {/* For rooms, show category selector */}
        {entityType === 'room' && (
          <Form.Item
            label="EM Category (Room Type)"
            name="emCategoryId"
            rules={[{ required: true, message: 'Please select an EM category' }]}
          >
            <Select
              placeholder="Select EM Category"
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={emCategories.map(cat => ({
                value: cat.id,
                label: cat.name,
              }))}
            />
          </Form.Item>
        )}

        {/* For rooms with features, show feature selection */}
        {entityType === 'room' && roomFeatures.length > 0 && (
          <>
            <Divider />
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <Title level={5} style={{ margin: 0 }}>
                  Select Features to Push as Tags ({selectedFeatures.length}/{roomFeatures.length})
                </Title>
                <div>
                  <Button size="small" onClick={handleSelectAllFeatures} style={{ marginRight: 8 }}>
                    Select All
                  </Button>
                  <Button size="small" onClick={handleDeselectAllFeatures}>
                    Deselect All
                  </Button>
                </div>
              </div>
              <div style={{
                maxHeight: 200,
                overflowY: 'auto',
                border: '1px solid #d9d9d9',
                borderRadius: 8,
                padding: 16,
                background: '#fafafa'
              }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: 8
                }}>
                  {roomFeatures.map(feature => {
                    const featureName = feature.names && Object.values(feature.names)[0];
                    return (
                      <Checkbox
                        key={feature.mewsId}
                        checked={selectedFeatures.includes(feature.mewsId)}
                        onChange={() => handleFeatureToggle(feature.mewsId)}
                      >
                        {featureName || 'Unnamed Feature'}
                      </Checkbox>
                    );
                  })}
                </div>
              </div>
            </div>
          </>
        )}

        <Divider />

        <Title level={5} style={{ marginBottom: 16 }}>Language Mapping</Title>

        <div style={{ maxHeight: 500, overflowY: 'auto', border: '1px solid #d9d9d9', borderRadius: 8 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{
                  padding: '12px 16px',
                  background: '#f0f5ff',
                  borderBottom: '2px solid #d9d9d9',
                  textAlign: 'left',
                  fontWeight: 600,
                  position: 'sticky',
                  top: 0,
                  zIndex: 1
                }}>
                  SQLite (Source)
                </th>
                <th style={{
                  width: '60px',
                  padding: '12px 16px',
                  background: '#f0f5ff',
                  borderBottom: '2px solid #d9d9d9',
                  textAlign: 'center',
                  position: 'sticky',
                  top: 0,
                  zIndex: 1
                }}></th>
                <th style={{
                  padding: '12px 16px',
                  background: '#e6f7ff',
                  borderBottom: '2px solid #d9d9d9',
                  textAlign: 'left',
                  fontWeight: 600,
                  position: 'sticky',
                  top: 0,
                  zIndex: 1
                }}>
                  EM (Destination)
                </th>
              </tr>
            </thead>
            <tbody>
              {sqliteLanguages.map((lang, index) => {
                const name = entityData?.emNames?.[lang] || entityData?.names?.[lang] || '';
                const description = entityData?.emDescriptions?.[lang] || entityData?.descriptions?.[lang] || '';

                return (
                  <tr key={lang} style={{
                    background: index % 2 === 0 ? '#fff' : '#fafafa'
                  }}>
                    <td style={{
                      padding: '16px',
                      borderBottom: '1px solid #f0f0f0',
                      verticalAlign: 'top'
                    }}>
                      <div>
                        <Text strong style={{ color: '#1890ff', fontSize: 14, display: 'block', marginBottom: 8 }}>
                          {lang}
                        </Text>
                        <div style={{ marginBottom: 8 }}>
                          <Text type="secondary" style={{ fontSize: 12 }}>Name:</Text>
                          <div style={{
                            marginTop: 4,
                            padding: 8,
                            background: '#fff',
                            border: '1px solid #e8e8e8',
                            borderRadius: 4,
                            fontSize: 13
                          }}>
                            {name || <Text type="secondary" italic>(no name)</Text>}
                          </div>
                        </div>
                        {description && (
                          <div>
                            <Text type="secondary" style={{ fontSize: 12 }}>Description:</Text>
                            <div style={{
                              marginTop: 4,
                              padding: 8,
                              background: '#fff',
                              border: '1px solid #e8e8e8',
                              borderRadius: 4,
                              fontSize: 12,
                              maxHeight: 100,
                              overflowY: 'auto',
                              color: '#666'
                            }}>
                              {stripHtml(description)}
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={{
                      padding: '16px',
                      borderBottom: '1px solid #f0f0f0',
                      textAlign: 'center',
                      verticalAlign: 'middle'
                    }}>
                      <ArrowRightOutlined style={{ fontSize: 18, color: '#1890ff' }} />
                    </td>
                    <td style={{
                      padding: '16px',
                      borderBottom: '1px solid #f0f0f0',
                      verticalAlign: 'top'
                    }}>
                      <div>
                        <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 8 }}>
                          Map {lang} to EM Language:
                        </Text>
                        <Form.Item
                          name={`mapping_${lang}`}
                          style={{ marginBottom: 0 }}
                        >
                          <Select
                            placeholder="Select EM language"
                            showSearch
                            filterOption={(input, option) =>
                              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            options={emLanguages.map(emLang => ({
                              value: emLang.id,
                              label: `${emLang.code}`,
                            }))}
                          />
                        </Form.Item>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: 24, textAlign: 'right' }}>
          <Button onClick={onClose} style={{ marginRight: 8 }}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            Push to EM
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default PushToEmModal;
